// Manual scroll anchoring for remote collab reconciles.
//
// The editor canvas scrolls the WINDOW (no inner scroll container) and has no
// overflow-anchor tuning, so when a peer adds/removes a slide ABOVE the local
// user's viewport, the content below shifts and the user's screen visibly
// "jumps". The contract we want: a remote change may adjust scrollTop, but the
// content the user is looking at must stay at the same place on screen.
//
// Usage: capture BEFORE applying the remote change to the DOM, restore AFTER
// the DOM has committed (the caller flushes the update synchronously):
//
//   const anchors = captureScrollAnchor();
//   flushSync(applyRemoteChange);
//   restoreScrollAnchor(anchors);

export type ScrollAnchor = { id: string; top: number };

// Height of the fixed editor header: a slide whose bottom is above this line
// is not actually visible, so it can't serve as the user's visual anchor.
// Matches the scroll offset used by useActiveThumbnail's scroll-to-slide.
const HEADER_OFFSET = 142;

const SLIDE_SELECTOR = '.storyContent [data-layer-type="slide"][data-layer-id]';

// The user's visual anchor, most precise first:
//
//   1. The DEEPEST entity under the top of the visible canvas (probed via
//      elementFromPoint) + its entity ancestors. Slide-level anchoring alone
//      can't compensate a shift INSIDE the anchor slide — if a peer adds
//      content at the top of the slide the user is reading, the slide's own
//      top doesn't move but everything below it does. Every entity level
//      renders data-layer-id (slide/combo/set/group/element), so the deep
//      anchor is always addressable.
//   2. The topmost visible slide + the slides after it, as fallbacks for when
//      the deep anchor (or its whole subtree) is what the remote change
//      deleted.
export function captureScrollAnchor(): ScrollAnchor[] | null {
  const anchors: ScrollAnchor[] = [];
  const seen = new Set<string>();
  const push = (el: Element | null) => {
    if (!el) return;
    const id = el.getAttribute("data-layer-id");
    if (!id || seen.has(id)) return;
    seen.add(id);
    anchors.push({ id, top: el.getBoundingClientRect().top });
  };

  // Deep probe. Best-effort: jsdom lacks elementFromPoint, an overlay can sit
  // above the canvas, the probe point can land in a slide gap — any miss just
  // falls through to the slide scan.
  try {
    if (typeof document.elementFromPoint === "function") {
      const canvas = document.querySelector(".storyContent");
      const canvasRect = canvas?.getBoundingClientRect();
      const x = canvasRect
        ? canvasRect.left + canvasRect.width / 2
        : window.innerWidth / 2;
      let deep =
        document.elementFromPoint(x, HEADER_OFFSET + 1)?.closest("[data-layer-id]") ??
        null;
      // Overlays (toolbars, floating panels) also live above the canvas —
      // only trust a hit inside the story canvas.
      if (deep && !deep.closest(".storyContent")) deep = null;
      while (deep) {
        push(deep);
        deep = deep.parentElement?.closest("[data-layer-id]") ?? null;
      }
    }
  } catch {
    // probe is an optimization; the slide scan below always runs
  }

  // Slide scan: topmost slide still visible below the fixed header, then the
  // slides after it.
  const slides = Array.from(
    document.querySelectorAll<HTMLElement>(SLIDE_SELECTOR),
  );
  let visibleFound = false;
  for (const el of slides) {
    if (!visibleFound && el.getBoundingClientRect().bottom <= HEADER_OFFSET)
      continue;
    visibleFound = true;
    push(el);
  }
  return anchors.length ? anchors : null;
}

// Re-measure the first surviving anchor and compensate the window scroll by
// its displacement, so the user's on-screen content does not move. If every
// captured anchor was deleted there is nothing stable to hold on to — skip.
export function restoreScrollAnchor(anchors: ScrollAnchor[] | null): void {
  if (!anchors?.length) return;
  for (const anchor of anchors) {
    const el = document.querySelector(
      `.storyContent [data-layer-id="${CSS.escape(anchor.id)}"]`,
    );
    if (!el) continue;
    const delta = el.getBoundingClientRect().top - anchor.top;
    if (delta) window.scrollBy({ top: delta, behavior: "instant" });
    return;
  }
}
